import React from 'react';
import { type LucideIcon, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'glass';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  children,
  disabled,
  ...props
}, ref) => {
  const variants = {
    primary: "bg-brand-primary text-white hover:bg-brand-primary/90 shadow-brand",
    secondary: "bg-white/10 text-white hover:bg-white/20 border border-white/10",
    ghost: "bg-transparent text-gray-400 hover:text-white hover:bg-white/5",
    outline: "bg-transparent border border-brand-primary text-brand-primary hover:bg-brand-primary/10",
    danger: "bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white",
    glass: "bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10"
  };

  const sizes = {
    sm: "min-h-10 px-3 py-1.5 text-xs rounded-lg gap-1.5",
    md: "min-h-11 px-6 py-2.5 text-[10px] rounded-xl gap-2",
    lg: "min-h-12 px-8 py-3.5 text-xs rounded-2xl gap-3",
    icon: "min-h-11 min-w-11 p-2 rounded-xl"
  };

  return (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        "inline-flex items-center justify-center font-black uppercase tracking-widest transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:active:scale-100 cursor-pointer select-none",
        "touch-manipulation",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="animate-spin" size={size === 'sm' || size === 'icon' ? 14 : 18} />
      ) : (
        <>
          {LeftIcon && <LeftIcon size={size === 'sm' || size === 'icon' ? 14 : 18} />}
          {children}
          {RightIcon && <RightIcon size={size === 'sm' || size === 'icon' ? 14 : 18} />}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';
